import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// Import concrete modules (not the @/lib and @/utils barrels): these hooks are
// instantiated at module-load time via createEntityCrudHooks(), and the barrels
// form a cycle back into @/hooks that leaves the factory undefined at that point.
import { supabase } from '@/lib/supabase';
import { handleSupabaseError, handleUnexpectedError } from '@/lib/supabase-error-handler';
import { logActivity, getObjectDiff } from '@/utils/activity-logger';

/**
 * CRUD hook factory for the simple "class taxonomy" tables — currently
 * `class_locations` and `class_categories`. Both are a title-only row with
 * created-by admin attribution, activity logging on every mutation, and a
 * cross-invalidation of the class list on delete. The two per-entity hook
 * files were byte-identical apart from the noun (and the by-id query-key
 * shape), so this factory holds the shared logic and each entity file is a
 * thin config + named re-exports.
 */
export interface EntityCrudHooksConfig {
  /** Postgres table name, e.g. 'class_locations'. */
  table: string;
  /** Activity-log entity type, e.g. 'class_location'. Actions are `${entityType}_created|updated|deleted`. */
  entityType: string;
  /** Singular human label for error/operation messages, e.g. 'class location'. */
  label: string;
  /** Plural human label, e.g. 'class locations'. */
  labelPlural: string;
  /** PascalCase noun for hookName telemetry, e.g. 'ClassLocation' → 'useCreateClassLocation'. */
  pascalName: string;
  /** List query-key root — a string constant from query-keys.ts. */
  allKey: string;
  /** Builds the by-id query key. Locations use a [prefix, id] tuple; categories use BY_ID(id). */
  byId: (id: string) => readonly unknown[];
  /** Extra list keys to invalidate after a delete (e.g. the class list). */
  relatedKeysOnDelete?: readonly string[];
}

export function createEntityCrudHooks<TEntity extends { id: string; title: string }, TCreateData, TUpdateData>(
  config: EntityCrudHooksConfig
) {
  const { table, entityType, label, labelPlural, pascalName, allKey, byId, relatedKeysOnDelete = [] } = config;

  function useList() {
    return useQuery({
      queryKey: [allKey],
      queryFn: async () => {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .order('title', { ascending: true });

        if (error) handleSupabaseError(error, { operation: `fetching ${labelPlural}` });
        return data as TEntity[];
      },
    });
  }

  function useOne(id: string) {
    return useQuery({
      queryKey: byId(id),
      queryFn: async () => {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .eq('id', id)
          .single();

        if (error) handleSupabaseError(error, { operation: `fetching ${label}` });
        return data as TEntity;
      },
      enabled: !!id,
    });
  }

  function useCreate() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (data: TCreateData) => {
        try {
          // Get current admin user details
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user?.id) {
            throw new Error(`You must be logged in to create ${labelPlural}`);
          }

          const { data: adminUser } = await supabase
            .from('admin_users')
            .select('email, full_name')
            .eq('id', session.user.id)
            .single();

          if (!adminUser) {
            throw new Error('Admin user not found');
          }

          const record = {
            ...(data as Record<string, unknown>),
            created_by_name: adminUser.full_name || adminUser.email,
            created_by_email: adminUser.email,
          };

          const { data: created, error } = await supabase
            .from(table)
            .insert([record])
            .select()
            .single();

          if (error) handleSupabaseError(error, { operation: `creating ${label}` });

          // Log activity
          await logActivity(
            `${entityType}_created`,
            entityType,
            created.id,
            undefined,
            record
          );

          return created as TEntity;
        } catch (error) {
          handleUnexpectedError(error, { hookName: `useCreate${pascalName}`, operation: `creating ${label}` });
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [allKey] });
      },
    });
  }

  function useUpdate(id: string) {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (data: TUpdateData) => {
        try {
          // Get the original record for change tracking
          const { data: original, error: fetchError } = await supabase
            .from(table)
            .select('*')
            .eq('id', id)
            .single();

          if (fetchError) handleSupabaseError(fetchError, { operation: `fetching ${label} for update` });

          const { data: updated, error } = await supabase
            .from(table)
            .update(data)
            .eq('id', id)
            .select()
            .single();

          if (error) handleSupabaseError(error, { operation: `updating ${label}` });

          // Log activity with changes
          const changes = getObjectDiff(original, updated);
          if (Object.keys(changes).length > 0) {
            await logActivity(
              `${entityType}_updated`,
              entityType,
              id,
              changes
            );
          }

          return updated as TEntity;
        } catch (error) {
          handleUnexpectedError(error, { hookName: `useUpdate${pascalName}`, operation: `updating ${label}` });
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [allKey] });
        queryClient.invalidateQueries({ queryKey: byId(id) });
      },
    });
  }

  function useDelete() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (id: string) => {
        try {
          // Get the record details before deletion for activity logging
          const { data: record, error: fetchError } = await supabase
            .from(table)
            .select('*')
            .eq('id', id)
            .single();

          if (fetchError) handleSupabaseError(fetchError, { operation: `fetching ${label} for deletion` });

          const { error } = await supabase
            .from(table)
            .delete()
            .eq('id', id);

          if (error) handleSupabaseError(error, { operation: `deleting ${label}` });

          // Log activity
          await logActivity(
            `${entityType}_deleted`,
            entityType,
            id,
            { title: { from: record.title, to: null } },
            { title: record.title }
          );
        } catch (error) {
          handleUnexpectedError(error, { hookName: `useDelete${pascalName}`, operation: `deleting ${label}` });
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [allKey] });
        for (const key of relatedKeysOnDelete) {
          queryClient.invalidateQueries({ queryKey: [key] });
        }
      },
    });
  }

  return { useList, useOne, useCreate, useUpdate, useDelete };
}
